import { ErrorInfo } from 'react';

import { devportalConfig } from 'shared/applicationInfo';
import { isServer } from 'shared/utils';
import { Logger } from 'types/Logger';
import {
  InboundOutBoundParams,
  ContextualParams,
  ReportParams,
  SessionInfo,
  applicationInfo,
} from 'types/SplunkLogs';

class SplunkReporter {
  private logger: Logger | undefined;
  private applicationInfo: applicationInfo;
  private serverSession: { [sessionId: string]: SessionInfo } = {};
  private clientSession: SessionInfo;
  private isEnabled: boolean;
  static _instance: SplunkReporter;

  constructor() {
    this.applicationInfo = devportalConfig;

    this.serverSession = {};

    this.clientSession = {};

    this.isEnabled = true;
  }

  public setIsEnabled(isEnabled: boolean) {
    this.isEnabled = isEnabled;
  }

  public setLogger(logger: Logger) {
    this.logger = logger;
  }

  public setAppImage(appImage: string) {
    this.applicationInfo.appImage = appImage;
  }

  public setClientSession(args: SessionInfo) {
    const getBrowserLocation = () => {
      if (isServer()) {
        return;
      }
      return window?.location?.href;
    };

    const fullUrl = this.clientSession?.fullUrl || getBrowserLocation();
    this.clientSession = { ...this.clientSession, ...args, fullUrl };
  }

  public setServerSession(sessionId: string, args: SessionInfo) {
    this.serverSession[sessionId] = { ...this.serverSession[sessionId], ...args };
  }

  public deleteServerSession(sessionId: string) {
    delete this.serverSession[sessionId];
  }

  public inbound({
    sessionId,
    logInfo,
    network,
    event,
    action,
    activityInfo,
    rum,
    error,
  }: InboundOutBoundParams) {
    logInfo.logType = 'inbound';
    this.report({ sessionId, logInfo, network, event, action, activityInfo, rum, error });
  }

  public outbound({
    sessionId,
    logInfo,
    network,
    event,
    action,
    activityInfo,
    rum,
    error,
  }: InboundOutBoundParams) {
    logInfo.logType = 'outbound';
    this.report({ sessionId, logInfo, network, event, action, activityInfo, rum, error });
  }

  public contextual({
    sessionId,
    logInfo,
    event,
    action,
    activityInfo,
    rum,
    error,
  }: ContextualParams) {
    logInfo.logType = 'contextual';
    this.report({ sessionId, logInfo, event, action, activityInfo, rum, error });
  }

  private report({
    sessionId = '',
    logInfo,
    event,
    action,
    activityInfo,
    network,
    rum,
    error,
  }: ReportParams) {
    try {
      // this is controlled by a feature flag, to avoid overloading splunk
      if (!this.isEnabled || !this.logger) {
        return;
      }

      const { logLevel } = logInfo;
      const timestamp = new Date().toISOString();
      let safeError: Record<string, string | number | ErrorInfo | undefined> | undefined = error;
      if (error instanceof Error) {
        safeError = (Object.getOwnPropertyNames(error) as (keyof typeof error)[]).reduce<
          Record<string, string | number | undefined | ErrorInfo>
        >(
          (acc, key) => {
            acc[key] = error[key];
            return acc;
          },
          { name: error.name }
        );
      }
      this.logger![logLevel]({
        timestamp,
        event,
        action,
        activityInfo,
        logInfo,
        network,
        rum,
        error: safeError,
        sessionInfo: isServer()
          ? this.serverSession[sessionId] || { sessionId }
          : this.clientSession,
        applicationInfo: this.applicationInfo,
        newSyntax: true,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  }

  static getInstance(): SplunkReporter {
    if (!this._instance) {
      this._instance = new SplunkReporter();
    }
    return this._instance;
  }
}

export { SplunkReporter };
